import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT, ONE_WORLD, NEOPOLIS } from "../content";
import { useEnquiry } from "../enquiryContext";
import { scrollToId } from "../hooks/useLenis";

const LINKS = [
  { label: "Overview", id: "overview" },
  { label: "Price", id: "price" },
  { label: "Gallery", id: "gallery" },
  { label: "Amenities", id: "amenities" },
  { label: "FAQ", id: "faq" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { openPopup } = useEnquiry();
  const onNeopolis = pathname.includes("sobha-neopolis");
  const current = onNeopolis ? NEOPOLIS : ONE_WORLD;
  const other = onNeopolis ? ONE_WORLD : NEOPOLIS;

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <header data-testid="site-header" className="fixed top-0 left-0 right-0 z-50 bg-ink border-b border-gold/20">
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex flex-col leading-none shrink-0" aria-label={`${current.name} home`}>
          <span className="font-serif-display text-[#F9F8F6] text-xl md:text-2xl">
            Sobha <span className="italic text-gold">{onNeopolis ? "Neopolis" : "One World"}</span>
          </span>
          <span className="mt-1 text-[8px] md:text-[9px] uppercase tracking-[0.3em] text-[#F9F8F6]/50">
            by Sobha Limited
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-[12px] font-medium uppercase tracking-[0.18em] text-[#F9F8F6]/70 hover:text-gold transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <Link
            to={`/${other.slug}`}
            data-testid="nav-other-project"
            className="text-[12px] font-medium uppercase tracking-[0.18em] text-gold hover:text-[#F9F8F6] transition-colors duration-300"
          >
            {other.name} →
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-5 shrink-0">
          <a
            data-testid="nav-phone-link"
            href={CONTACT.phoneHref}
            className="flex items-center gap-2 text-[13px] font-medium text-[#F9F8F6]/85 hover:text-gold transition-colors"
          >
            <Phone size={14} strokeWidth={1.75} />
            {CONTACT.phone}
          </a>
          <button
            data-testid="nav-cost-sheet-button"
            onClick={() => openPopup({ project: current, intent: "Get Cost Sheet" })}
            className="bg-gold text-ink text-[12px] font-semibold uppercase tracking-[0.18em] px-6 py-3 hover:bg-[#F9F8F6] transition-colors duration-300"
          >
            Get Cost Sheet
          </button>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          className="lg:hidden p-2 text-[#F9F8F6]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-ink border-t border-gold/15"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left text-sm font-medium uppercase tracking-[0.18em] text-[#F9F8F6]/80"
                >
                  {l.label}
                </button>
              ))}
              <Link
                to={`/${other.slug}`}
                data-testid="nav-mobile-other-project"
                onClick={() => setOpen(false)}
                className="text-left text-sm font-medium uppercase tracking-[0.18em] text-gold"
              >
                Explore {other.name} →
              </Link>
              <button
                data-testid="nav-mobile-cost-sheet-button"
                onClick={() => {
                  setOpen(false);
                  openPopup({ project: current, intent: "Get Cost Sheet" });
                }}
                className="bg-gold text-ink text-[12px] font-semibold uppercase tracking-[0.18em] px-6 py-4 mt-2"
              >
                Get Cost Sheet
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
